import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { missionVision, founder, advantages, honors, companyGallery, softwareProducts } from '@/mocks/aboutData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setVisible(true);
        },
        { threshold }
      );
      observer.observe(node);
      observerRef.current = observer;
    }
  }, [threshold]);

  return { visible, ref };
}

interface AnchorNavProps {
  items: { id: string; label: string }[];
  activeId: string;
  onClick: (id: string) => void;
}

function AnchorNav({ items, activeId, onClick }: AnchorNavProps) {
  const navRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={navRef}
      className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm"
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex gap-1 md:gap-2 overflow-x-auto py-3 no-scrollbar">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => onClick(item.id)}
              className={`shrink-0 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                activeId === item.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:bg-surface hover:text-primary'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface LightboxProps {
  image: string;
  title: string;
  onClose: () => void;
}

function Lightbox({ image, title, onClose }: LightboxProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
        aria-label="关闭"
      >
        <i className="ri-close-line text-xl" />
      </button>
      <div
        className="relative bg-white rounded-xl p-4 md:p-6 max-w-4xl w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center">
          <img
            src={image}
            alt={title}
            className="max-h-[70vh] w-auto object-contain rounded-lg"
          />
        </div>
        <p className="text-center text-sm text-text-secondary mt-4 font-medium">
          {title}
        </p>
      </div>
    </div>
  );
}

const anchorItems = [
  { id: 'intro', label: '公司介绍' },
  { id: 'mission', label: '使命愿景' },
  { id: 'honors', label: '荣誉资质' },
  { id: 'founder', label: '创始人' },
  { id: 'gallery', label: '企业风采' },
  { id: 'advantages', label: '差异化优势' },
];

export default function About() {
  const introRef = useVisible(0.1);
  const honorsRef = useVisible(0.1);
  const mvRef = useVisible(0.1);
  const founderRef = useVisible(0.1);
  const galleryRef = useVisible(0.1);
  const advRef = useVisible(0.1);

  const [activeAnchor, setActiveAnchor] = useState('intro');
  const [lightbox, setLightbox] = useState<{ image: string; title: string } | null>(null);

  const sectionRefs = useRef<Record<string, HTMLElement>>({});

  const setSectionRef = useCallback((id: string) => {
    return (el: HTMLDivElement | null) => {
      if (el) sectionRefs.current[id] = el;
    };
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 56;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveAnchor(entry.target.getAttribute('data-anchor') || 'intro');
          }
        });
      },
      { rootMargin: '-56px 0px -60% 0px', threshold: 0 }
    );

    Object.entries(sectionRefs.current).forEach(([, el]) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const openLightbox = useCallback((image: string, title: string) => {
    setLightbox({ image, title });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(null);
  }, []);

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#2c1810] via-[#3d2518] to-[#2c1810] overflow-hidden">
        {/* 网格纹理 */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        {/* 暖色光晕 - 右上 */}
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.15] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.3) 0%, transparent 70%)' }}
        />
        {/* 柔和光晕 - 左下 */}
        <div
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-[0.12] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.2) 0%, transparent 70%)' }}
        />
        {/* 装饰圆环 */}
        <div className="absolute top-[18%] right-[10%] w-[200px] h-[200px] rounded-full border border-[#f59e0b]/10 pointer-events-none" />
        <div className="absolute top-[22%] right-[13%] w-[140px] h-[140px] rounded-full border border-[#f59e0b]/10 pointer-events-none" />
        <div className="absolute bottom-[18%] left-[8%] w-[180px] h-[180px] rounded-full border border-[#f59e0b]/10 pointer-events-none" />
        {/* 浮动光点 */}
        <div className="absolute top-24 right-[30%] w-2 h-2 bg-[#f59e0b]/30 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-28 left-[22%] w-1.5 h-1.5 bg-[#f59e0b]/20 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-36 left-[40%] w-1 h-1 bg-white/15 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '2s' }} />
        {/* 装饰线条 */}
        <div className="absolute top-[15%] right-[5%] w-px h-[250px] bg-gradient-to-b from-transparent via-[#f59e0b]/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-[20%] left-[5%] w-px h-[200px] bg-gradient-to-b from-transparent via-[#f59e0b]/10 to-transparent pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              融慧数科
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              快消行业营销数字化最佳实践者
            </p>
          </div>
        </div>
      </section>

      {/* Anchor Navigation */}
      <AnchorNav
        items={anchorItems}
        activeId={activeAnchor}
        onClick={scrollToSection}
      />

      {/* Company Intro */}
      <section
        ref={(el) => {
          introRef.ref(el);
          setSectionRef('intro')(el);
        }}
        data-anchor="intro"
        className="py-16 md:py-24 bg-white"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2
              className={`text-xl md:text-3xl font-bold text-primary mb-6 transition-all duration-500 ${
                introRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              公司介绍
            </h2>

            {/* Core positioning statement */}
            <div
              className={`mb-10 transition-all duration-500 delay-100 ${
                introRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-base md:text-lg text-text-secondary leading-relaxed">
                深圳融慧数科有限公司由实战经验丰富的营销数字化专家创立，
                <span className="text-accent font-semibold">深耕快消行业</span>。
                公司立足<strong className="text-primary">品牌方视角</strong>，通过
                <strong className="text-primary">五位一体营销数字化</strong>，
                以<strong className="text-primary">物码为底座</strong>，
                助力客户构建营销数字化管理体系，驱动业绩增长与提质增效。
              </p>
            </div>

            {/* Four core pillars */}
            <div
              className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 transition-all duration-500 delay-200 ${
                introRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {[
                { title: '运营精细化', desc: '数据驱动的精细化运营体系' },
                { title: '终端精益化', desc: '终端门店精益管理与效能提升' },
                { title: '渠道可视化', desc: '全链路渠道透明可视化管理' },
                { title: '决策数字化', desc: '智能数据支撑科学决策' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-surface rounded-xl p-5 border border-gray-100 text-center hover:border-accent/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-3">
                    <i className="ri-check-double-line text-accent" />
                  </div>
                  <h3 className="text-sm font-bold text-primary mb-1">{item.title}</h3>
                  <p className="text-xs text-text-muted leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Detailed description */}
            <div
              className={`bg-surface rounded-xl p-6 md:p-8 border border-gray-100 transition-all duration-500 delay-300 ${
                introRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed">
                凭借丰富的数字化转型经验与专业工具，融慧数科已成功服务食品饮料、日化、调味品、啤酒、乳制品等多个细分领域，帮助品牌实现从渠道管理到消费者运营的全链路数字化升级。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Promo Video */}
      <section className="py-8 md:py-12 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div
              className={`transition-all duration-500 ${
                introRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="bg-surface rounded-xl overflow-hidden border border-gray-100">
                <div className="aspect-video bg-black relative">
                  <video
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                    playsInline
                  >
                    <source
                      src="/images/about-promo-video.mp4"
                      type="video/mp4"
                    />
                    您的浏览器不支持视频播放
                  </video>
                </div>
                <div className="p-4 md:p-5 text-center">
                  <p className="text-sm font-medium text-primary">融慧数科宣传片</p>
                  <p className="text-xs text-text-muted mt-1">60秒快速了解融慧数科</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Vision Values */}
      <section
        ref={(el) => {
          mvRef.ref(el);
          setSectionRef('mission')(el);
        }}
        data-anchor="mission"
        className="py-16 md:py-24 bg-surface"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {Object.values(missionVision).map((item, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-6 md:p-8 border border-gray-100 text-center hover:border-accent/30 transition-all duration-300 ${
                  mvRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-2xl text-white`} />
                </div>
                <h3 className="text-lg font-bold text-primary mb-3">{item.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honors & Certifications */}
      <section
        ref={(el) => {
          honorsRef.ref(el);
          setSectionRef('honors')(el);
        }}
        data-anchor="honors"
        className="py-16 md:py-24 bg-white"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* 荣誉资质 */}
          <div className="mb-10 md:mb-14 text-center">
            <h2
              className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              荣誉资质
            </h2>
            <p
              className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              权威机构认证，值得信赖的数字化服务商
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {honors.map((item, index) => (
              <button
                key={index}
                onClick={() => openLightbox(item.image, item.title)}
                className={`group text-left bg-surface rounded-xl overflow-hidden border border-gray-100 hover:border-accent/30 transition-all duration-300 cursor-pointer ${
                  honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-white p-6 md:p-8 flex items-center justify-center relative">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 shadow-lg">
                      <i className="ri-zoom-in-line text-primary text-lg" />
                    </div>
                  </div>
                </div>
                <div className="p-5 md:p-6 text-center border-t border-gray-100">
                  <h3 className="text-base md:text-lg font-bold text-primary mb-1">{item.title}</h3>
                  <p className="text-sm text-text-muted">{item.description}</p>
                </div>
              </button>
            ))}
          </div>

          {/* 软件产品展示 */}
          <div className="mt-16 md:mt-24">
            <div className="mb-10 md:mb-14 text-center">
              <h2
                className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                  honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                软件产品
              </h2>
              <p
                className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                  honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                自主研发的核心产品，覆盖快消营销全链路
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {softwareProducts.map((item, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(item.image, item.title)}
                  className={`group text-left bg-surface rounded-xl overflow-hidden border border-gray-100 hover:border-accent/30 transition-all duration-300 cursor-pointer ${
                    honorsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-white flex items-center justify-center relative">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 shadow-lg">
                        <i className="ri-zoom-in-line text-primary text-lg" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4 md:p-5 text-center border-t border-gray-100">
                    <h3 className="text-sm md:text-base font-bold text-primary">{item.title}</h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section
        ref={(el) => {
          founderRef.ref(el);
          setSectionRef('founder')(el);
        }}
        data-anchor="founder"
        className="py-16 md:py-24 bg-surface-warm"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
            {/* Founder Avatar */}
            <div
              className={`shrink-0 mx-auto lg:mx-0 transition-all duration-500 ${
                founderRef.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
            >
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-2 border-accent/30">
                <img
                  src="/images/founder-dongwenbo.jpg"
                  alt="董文波"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* Founder Info */}
            <div className="flex-1 text-center lg:text-left">
              {/* Name */}
              <div
                className={`mb-5 transition-all duration-500 delay-100 ${
                  founderRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <h3 className="text-2xl md:text-3xl font-bold text-primary">董文波</h3>
              </div>

              {/* Main Intro */}
              <p
                className={`text-sm md:text-base text-text-secondary leading-relaxed mb-8 transition-all duration-500 delay-200 ${
                  founderRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                深圳融慧数科有限公司创始人，营销数字化专家、PMP项目管理专家，深耕信息化与数字化领域近20载，2024年提出"五位一体营销数字化"，拥有快消行业全周期数字化建设与运营实战经验。
              </p>

              {/* Experience, Titles & Awards Grid */}
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-5 transition-all duration-500 delay-300 ${
                  founderRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                {/* Experience */}
                <div className="md:col-span-2 bg-white rounded-xl p-5 md:p-6 border border-gray-100 text-left shadow-sm">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-4">
                    <i className="ri-road-map-line mr-2" />
                    人物经历
                  </h4>
                  <div className="space-y-3">
                    <div className="flex gap-3 items-start">
                      <span className="shrink-0 inline-block px-2 py-0.5 bg-accent/10 text-accent text-xs font-semibold rounded">2012</span>
                      <p className="text-sm text-text-secondary leading-relaxed">加入东鹏饮料，亲历并主导东鹏饮料从0到1的营销数字化体系建设，支撑企业高速成长。</p>
                    </div>
                    <div className="flex gap-3 items-start">
                      <span className="shrink-0 inline-block px-2 py-0.5 bg-accent/10 text-accent text-xs font-semibold rounded">2024</span>
                      <p className="text-sm text-text-secondary leading-relaxed">创立融慧数科，业务已覆盖食品饮料、日化、调味品、啤酒、乳制品等多个行业细分领域，服务客户包括白象、健力宝、百味佳、立白、蓝带、蒙牛、康师傅等企业。</p>
                    </div>
                  </div>
                </div>

                {/* Social Title */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 text-left shadow-sm">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-3">
                    <i className="ri-award-line mr-2" />
                    社会头衔
                  </h4>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    中山大学岭南学院数字经济专业硕士业界导师
                  </p>
                </div>

                {/* Honors */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 text-left shadow-sm">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-3">
                    <i className="ri-trophy-line mr-2" />
                    所获荣誉
                  </h4>
                  <ul className="space-y-2">
                    <li className="text-sm text-text-secondary leading-relaxed flex items-start gap-2">
                      <i className="ri-check-line text-accent shrink-0 mt-0.5" />
                      腾讯云TVP行业大使
                    </li>
                    <li className="text-sm text-text-secondary leading-relaxed flex items-start gap-2">
                      <i className="ri-check-line text-accent shrink-0 mt-0.5" />
                      CGCA第七届中国消费品行业CIO大会消费品行业CIO年度数字化领军人物奖
                    </li>
                    <li className="text-sm text-text-secondary leading-relaxed flex items-start gap-2">
                      <i className="ri-check-line text-accent shrink-0 mt-0.5" />
                      2025微信公开课年度数字化先锋个人
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team & Company Gallery */}
      <section
        ref={(el) => {
          galleryRef.ref(el);
          setSectionRef('gallery')(el);
        }}
        data-anchor="gallery"
        className="py-16 md:py-24 bg-surface"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-10 md:mb-14 text-center">
            <h2
              className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                galleryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              企业风采
            </h2>
            <p
              className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                galleryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              专业的团队，卓越的服务
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {companyGallery.map((img, index) => (
              <button
                key={index}
                onClick={() => openLightbox(img.src, img.caption)}
                className={`group relative overflow-hidden rounded-xl bg-white border border-gray-100 hover:border-accent/30 transition-all duration-500 text-left cursor-pointer ${
                  galleryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 shadow-lg">
                      <i className="ri-zoom-in-line text-primary text-lg" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section
        ref={(el) => {
          advRef.ref(el);
          setSectionRef('advantages')(el);
        }}
        data-anchor="advantages"
        className="py-16 md:py-24 bg-white"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-10 md:mb-14">
            <h2
              className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                advRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              三大差异化优势
            </h2>
            <p
              className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                advRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              为什么选择融慧数科
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {advantages.map((adv, index) => (
              <div
                key={index}
                className={`relative bg-surface rounded-xl p-6 md:p-8 border border-gray-100 overflow-hidden hover:border-accent/30 transition-all duration-300 group ${
                  advRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + index * 120}ms` }}
              >
                {/* 顶部 accent 装饰条 */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-accent" />

                {/* 大号背景数字 */}
                <div className="absolute top-3 right-4 text-6xl md:text-7xl font-bold text-gray-100 select-none pointer-events-none">
                  {adv.num}
                </div>

                <div className="relative z-10">
                  {/* 序号标签 */}
                  <div className="inline-flex items-center gap-2 mb-5">
                    <span className="w-8 h-8 flex items-center justify-center bg-accent/10 rounded-lg text-sm font-bold text-accent">
                      {adv.num}
                    </span>
                  </div>

                  {/* 主标题 */}
                  <h3 className="text-xl md:text-2xl font-bold text-primary mb-2">
                    {adv.title}
                  </h3>

                  {/* 副标题 */}
                  <p className="text-sm font-semibold text-accent mb-4">
                    {adv.subtitle}
                  </p>

                  {/* 描述 */}
                  <p className="text-sm md:text-base text-text-secondary leading-relaxed">
                    {(() => {
                      const parts = adv.desc.split('**');
                      if (parts.length === 1) return adv.desc;
                      return parts.map((part, i) =>
                        i % 2 === 1 ? (
                          <strong key={i} className="text-primary">{part}</strong>
                        ) : (
                          part
                        )
                      );
                    })()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-surface-warm">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-primary mb-4">
            了解融慧数科如何助力您的品牌
          </h2>
          <p className="text-sm md:text-base text-text-muted mb-8 max-w-xl mx-auto">
            与我们的顾问团队沟通，获取定制化的营销数字化方案。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent-dark transition-colors"
          >
            <i className="ri-calendar-check-line" />
            预约沟通
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          image={lightbox.image}
          title={lightbox.title}
          onClose={closeLightbox}
        />
      )}
    </main>
  );
}